import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Refresh as RefreshIcon, Send as SendIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

import { getBackendUrl } from '../config/urls';

const BACKEND_URL = getBackendUrl();

const Transfer = () => {
  const { token } = useAuth();
  const [wallets, setWallets] = useState([]);
  const [tokenMonitors, setTokenMonitors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Dialog states
  const [addTokenDialog, setAddTokenDialog] = useState(false);
  const [transferDialog, setTransferDialog] = useState(false);
  const [selectedMonitor, setSelectedMonitor] = useState(null);

  // Form states
  const [addTokenForm, setAddTokenForm] = useState({
    walletId: '',
    tokenAddress: '',
    tokenName: '',
    tokenSymbol: ''
  });

  // Transfer states
  const [transferring, setTransferring] = useState(false);
  const [transferStep, setTransferStep] = useState('');
  const [transferMessage, setTransferMessage] = useState('');
  const [useFlashbots, setUseFlashbots] = useState(false);
  const [useRegularTransaction, setUseRegularTransaction] = useState(false); // NEW: Choose transaction type

  // Load data on component mount
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);

      // Load wallets
      const walletsResponse = await fetch(`${BACKEND_URL}/api/wallets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (walletsResponse.ok) {
        const walletsData = await walletsResponse.json();
        // Filter to only show delegated wallets
        const delegatedWallets = walletsData.success ? 
          (walletsData.wallets || []).filter(wallet => wallet.isDelegated) : [];
        setWallets(delegatedWallets);
      }

      // Load token monitors
      const monitorsResponse = await fetch(`${BACKEND_URL}/api/transfer/monitors`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (monitorsResponse.ok) {
        const monitorsData = await monitorsResponse.json();
        setTokenMonitors(monitorsData.success ? monitorsData.monitors || [] : []);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddTokenMonitor = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/transfer/monitor`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(addTokenForm)
      });

      const data = await response.json();
      if (data.success) {
        setSuccess('Token monitor added successfully');
        setAddTokenDialog(false);
        setAddTokenForm({
          walletId: '',
          tokenAddress: '',
          tokenName: '',
          tokenSymbol: ''
        });
        loadData();
      } else {
        setError(data.error || 'Failed to add token monitor');
      }
    } catch (error) {
      setError('Failed to add token monitor');
      console.error('Error adding token monitor:', error);
    }
  };

  const handleRemoveTokenMonitor = async (monitorId) => {
    if (!window.confirm('Are you sure you want to remove this token monitor?')) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/transfer/monitor/${monitorId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = await response.json();
      if (data.success) {
        setSuccess('Token monitor removed successfully');
        loadData();
      } else {
        setError(data.error || 'Failed to remove token monitor');
      }
    } catch (error) {
      setError('Failed to remove token monitor');
      console.error('Error removing token monitor:', error);
    }
  };

  const handleCheckBalance = async (monitorId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/transfer/check-balance/${monitorId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = await response.json();
      if (data.success) {
        setSuccess(`Balance checked: ${data.balance} ${data.symbol}`);
        loadData(); // Refresh to show updated balance
      } else {
        setError(data.error || 'Failed to check balance');
      }
    } catch (error) {
      setError('Failed to check balance');
      console.error('Error checking balance:', error);
    }
  };

  const handleTransfer = async (monitor) => {
    setSelectedMonitor(monitor);
    setUseFlashbots(false); // Reset Flashbots checkbox
    setUseRegularTransaction(false); // Reset regular transaction checkbox
    setTransferDialog(true);
  };

  const executeTransfer = async () => {
    if (!selectedMonitor) return;

    setTransferring(true);
    setTransferStep('initializing');
    setTransferMessage('Starting transfer...');

    try {
      const response = await fetch(`${BACKEND_URL}/api/transfer/execute`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          monitorId: selectedMonitor.id,
          useFlashbots: useFlashbots,
          useRegularTransaction: useRegularTransaction // NEW: Choose transaction type
        })
      });

      setTransferStep('processing');
      setTransferMessage('Processing transfer...');

      const data = await response.json();
      if (data.success) {
        let successMessage = `Transfer successful!`;
        
        // Add submission method information
        if (data.method && data.method.includes('flashbots')) {
          successMessage += ` Submitted via ${data.method} (Bundle: ${data.txHash})`;
          if (data.targetBlock) {
            successMessage += ` Target Block: ${data.targetBlock}`;
          }
        } else {
          successMessage += ` Transaction Hash: ${data.txHash}`;
        }
        
        setSuccess(successMessage);
        setTransferDialog(false);
        loadData();
      } else {
        setError(data.error || 'Failed to execute transfer');
      }
    } catch (error) {
      setError('Failed to execute transfer');
      console.error('Error executing transfer:', error);
    } finally {
      setTransferring(false);
      setTransferStep('');
      setTransferMessage('');
    }
  };

  const formatAddress = (address) => {
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const formatBalance = (balance, decimals = 18) => {
    if (!balance) return '0';
    const num = parseFloat(balance) / Math.pow(10, decimals);
    return num.toFixed(6);
  };

  const getWalletAddress = (walletId) => {
    const wallet = wallets.find(w => w.id === walletId);
    return wallet ? wallet.address : 'Unknown';
  };

  const getStatusColor = (balance) => {
    const numBalance = parseFloat(balance || '0');
    if (numBalance > 1) return 'success';
    if (numBalance > 0) return 'warning';
    return 'error';
  };

  return (
    <Box
      component="main"
      sx={{
        width: '100%',
        maxWidth: { xs: '100%', lg: 1440 },
        mx: 'auto',
        px: { xs: 2, sm: 2.5, md: 3 },
        py: { xs: 2, md: 3 },
        pb: { xs: 4, md: 5 },
      }}
    >
      <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
        Token Transfer
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Paper sx={{ p: { xs: 2, sm: 2.5 }, mb: 3 }}>
        <Box
          display="flex"
          flexDirection={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', sm: 'center' }}
          gap={2}
          mb={2}
        >
          <Typography variant="h6">Token Monitors</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddTokenDialog(true)}
            disabled={wallets.length === 0}
            sx={{ width: { xs: '100%', sm: 'auto' }, alignSelf: { sm: 'center' } }}
          >
            Add Token Monitor
          </Button>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Wallet</TableCell>
                <TableCell>Token</TableCell>
                <TableCell>Token Address</TableCell>
                <TableCell>Balance</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tokenMonitors.map((monitor) => (
                <TableRow key={monitor.id}>
                  <TableCell>
                    <Typography variant="body2" fontFamily="monospace">
                      {formatAddress(getWalletAddress(monitor.wallet_id))}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        {monitor.token_name || 'Unknown'}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {monitor.token_symbol || 'N/A'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontFamily="monospace">
                      {formatAddress(monitor.token_address)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={`${formatBalance(monitor.balance, monitor.decimals || 18)} ${monitor.token_symbol || ''}`}
                      color={getStatusColor(monitor.balance)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={1}>
                      <IconButton
                        size="small"
                        onClick={() => handleCheckBalance(monitor.id)}
                        disabled={loading}
                      >
                        <RefreshIcon />
                      </IconButton>
                      {parseFloat(monitor.balance || '0') > 1 && (
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleTransfer(monitor)}
                        >
                          <SendIcon />
                        </IconButton>
                      )}
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveTokenMonitor(monitor.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {tokenMonitors.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography color="textSecondary">
                      {wallets.length === 0 
                        ? 'No delegated wallets available. Please delegate a wallet in the Delegation tab first.'
                        : 'No token monitors found. Add one to get started.'
                      }
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add Token Monitor Dialog */}
      <Dialog open={addTokenDialog} onClose={() => setAddTokenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Token Monitor</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Wallet</InputLabel>
                <Select
                  value={addTokenForm.walletId}
                  onChange={(e) => setAddTokenForm(form => ({ ...form, walletId: e.target.value }))}
                  label="Wallet"
                  disabled={wallets.length === 0}
                >
                  {wallets.length === 0 ? (
                    <MenuItem disabled>
                      No delegated wallets available. Please delegate a wallet first.
                    </MenuItem>
                  ) : (
                    wallets.map((wallet) => (
                      <MenuItem key={wallet.id} value={wallet.id}>
                        {formatAddress(wallet.address)} ({wallet.chains})
                      </MenuItem>
                    ))
                  )}
                </Select>
                {wallets.length === 0 && (
                  <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
                    Only delegated wallets can be used for token transfers. Please delegate a wallet in the Delegation tab first.
                  </Typography>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Token Address"
                value={addTokenForm.tokenAddress}
                onChange={(e) => setAddTokenForm(form => ({ ...form, tokenAddress: e.target.value }))}
                placeholder="0x..."
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Token Name"
                value={addTokenForm.tokenName}
                onChange={(e) => setAddTokenForm(form => ({ ...form, tokenName: e.target.value }))}
                placeholder="e.g., Wrapped Ether"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Token Symbol"
                value={addTokenForm.tokenSymbol}
                onChange={(e) => setAddTokenForm(form => ({ ...form, tokenSymbol: e.target.value }))}
                placeholder="e.g., WETH"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddTokenDialog(false)}>Cancel</Button>
          <Button onClick={handleAddTokenMonitor} variant="contained">
            Add Monitor
          </Button>
        </DialogActions>
      </Dialog>

      {/* Transfer Dialog */}
      <Dialog open={transferDialog} onClose={() => setTransferDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Execute Transfer</DialogTitle>
        <DialogContent>
          {selectedMonitor && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body1" gutterBottom>
                Transfer {selectedMonitor.token_name} ({selectedMonitor.token_symbol})
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                From: {formatAddress(getWalletAddress(selectedMonitor.wallet_id))}
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Balance: {formatBalance(selectedMonitor.balance, selectedMonitor.decimals || 18)} {selectedMonitor.token_symbol}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Token: {formatAddress(selectedMonitor.token_address)}
              </Typography>
              
              {/* Flashbots Options */}
              <Box sx={{ mt: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  🚀 Flashbots Options (MEV Protection)
                </Typography>
                
                <Box sx={{ mb: 1 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={useFlashbots}
                        onChange={(e) => setUseFlashbots(e.target.checked)}
                        disabled={transferring}
                      />
                    }
                    label="Use Flashbots (MEV Protection)"
                  />
                  {useFlashbots && (
                    <Alert severity="info" sx={{ mt: 1 }}>
                      <Typography variant="body2">
                        🛡️ MEV Protection enabled: Your transfer will be submitted via Flashbots for protection against sandwich attacks
                      </Typography>
                    </Alert>
                  )}
                </Box>
                
                {useFlashbots && (
                  <Box sx={{ mb: 1 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={useRegularTransaction}
                          onChange={(e) => setUseRegularTransaction(e.target.checked)}
                          disabled={transferring}
                        />
                      }
                      label="Use Regular Transaction (Recommended for Flashbots)"
                    />
                    {useRegularTransaction && (
                      <Alert severity="success" sx={{ mt: 1 }}>
                        <Typography variant="body2">
                          ✅ Regular transaction + Flashbots: Best compatibility and MEV protection
                        </Typography>
                      </Alert>
                    )}
                    {!useRegularTransaction && (
                      <Alert severity="warning" sx={{ mt: 1 }}>
                        <Typography variant="body2">
                          ⚠️ EIP-7702 + Flashbots: May not work due to Flashbots compatibility issues
                        </Typography>
                      </Alert>
                    )}
                  </Box>
                )}
              </Box>
              
              {transferring && (
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <CircularProgress size={24} sx={{ mr: 1 }} />
                  <Typography variant="body2">
                    {transferMessage}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTransferDialog(false)} disabled={transferring}>
            Cancel
          </Button>
          <Button 
            onClick={executeTransfer} 
            variant="contained" 
            disabled={transferring}
            startIcon={transferring ? <CircularProgress size={16} /> : <SendIcon />}
          >
            {transferring ? 'Transferring...' : 'Execute Transfer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Transfer; 